import type { ReactNode } from 'react';
import { Eye } from 'lucide-react';
import type { Permission } from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { Button, DescriptionList, Modal, NotLinkedNotice, PRODUCTION_LINKING_HINT, type ModalSize } from '@/components/ui';
import type { DescriptionItem } from '@/components/ui/Card';
import { NotLinkedBadge } from '../../components/StatusBadges';

interface NotLinkedActionModalProps {
  title: string;
  description: string;
  icon: ReactNode;
  size?: ModalSize;
  /** Explains which production system owns this data. */
  notice: ReactNode;
  /** Central Admin data that IS known (e.g. planned/requested values). */
  known?: readonly DescriptionItem[];
  /** Production fields that are not linked yet. */
  notLinked: readonly string[];
  /** Read-type production operations (anyone who can open the dialog); disabled until production linking. */
  viewActions?: readonly string[];
  /** Production operations requiring `managePermission`; hidden without it, otherwise disabled until production linking. */
  actions: readonly string[];
  managePermission: Permission;
  children?: ReactNode;
  onClose: () => void;
}

/**
 * Shared layout for operational dialogs (license, updates, API, devices,
 * mobile). They keep their Phase 1 structure but perform no production
 * operation. Two separate concepts decide what is shown:
 * - permission: management actions are hidden from roles without `managePermission`;
 * - availability: every action is disabled until production linking exists.
 */
export function NotLinkedActionModal({
  title,
  description,
  icon,
  size = 'md',
  notice,
  known = [],
  notLinked,
  viewActions = [],
  actions,
  managePermission,
  children,
  onClose,
}: NotLinkedActionModalProps) {
  const canManage = useCan()(managePermission);
  const visible = [...viewActions, ...(canManage ? actions : [])];
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={description}
      titleAside={<NotLinkedBadge />}
      icon={icon}
      size={size}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} className="mr-auto">
            Close
          </Button>
          {visible.map((label, i) => (
            <Button key={label} variant={i === visible.length - 1 ? 'primary' : 'secondary'} disabled title={PRODUCTION_LINKING_HINT} aria-describedby="not-linked-hint">
              {label}
            </Button>
          ))}
        </>
      }
    >
      <NotLinkedNotice className="mb-5">{notice}</NotLinkedNotice>
      <DescriptionList items={[...known, ...notLinked.map((label) => ({ label, value: <NotLinkedBadge /> }))]} />
      {children}
      {!canManage && (
        <p className="mt-5 flex items-start gap-2 rounded-md border border-line bg-surface-muted px-3 py-2 text-[13px] text-ink-secondary">
          <Eye size={15} aria-hidden="true" className="mt-0.5 shrink-0 text-ink-muted" />
          View only: your role can see this information but cannot manage it.
        </p>
      )}
      {visible.length > 0 && (
        <p id="not-linked-hint" className="mt-5 text-xs text-ink-muted">
          Actions are disabled: {PRODUCTION_LINKING_HINT.toLowerCase()}.
        </p>
      )}
    </Modal>
  );
}
